import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaStar,
  FaTable,
  FaTh,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import HomeClampedText from '../../components/HomeClampedText';
import InfrastructureLeasingAcceptRequirementQuotationModal from '../../components/infrastructureLeasing/InfrastructureLeasingAcceptRequirementQuotationModal';
import InfrastructureLeasingRejectionDetailsModal from '../../components/infrastructureLeasing/InfrastructureLeasingRejectionDetailsModal';
import InfrastructureLeasingSellerReviewModal from '../../components/infrastructureLeasing/InfrastructureLeasingSellerReviewModal';
import {
  fetchMyInfrastructureEnquiries,
  postBuyerAcceptRequirementQuotation,
  postBuyerUnwindPendingSignatureDeal,
  postBuyerWithdrawSubmittedEnquiry,
} from '../../services/infrastructureLeasingListing.service';
import { formatInfrastructureLeasingUrgency } from '../../constants/infrastructureLeasingUrgencyOptions';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_search_listings.scss';
import '../../assets/styles/infrastructure_leasing_buyer_enquiries.scss';

const LAYOUT_KEY = 'ile_buyer_enquiries_layout';

/** When true, “Submit review” appears only after lease is active (stricter than payment completed). */
const REVIEW_REQUIRES_LEASE_ACTIVE = false;

function readStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'table' || v === 'cards') return v;
  } catch {
    /* ignore */
  }
  return 'cards';
}

/** Tab query for public listing detail — best-effort from enquiry asset type */
function listingTabForEnquiry(item) {
  const t = String(item.assetType || '').toLowerCase();
  if (t.includes('machin') || t.includes('equipment') || t.includes('asset')) return 'equipment';
  return 'real-estate';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function truncateMessage(s, max = 120) {
  if (s == null || String(s).trim() === '') return '—';
  const t = String(s).trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

function urgencyLabel(u) {
  return formatInfrastructureLeasingUrgency(u);
}

function isoDatePrefixFromEnquiry(startDate) {
  const s = String(startDate || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

function addMonthsToIsoStart(startIso, durationMonths) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso)) return '';
  const m = Math.trunc(Number(durationMonths)) || 0;
  if (m <= 0) return '';
  const d = new Date(`${startIso}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCMonth(d.getUTCMonth() + m);
  return d.toISOString().slice(0, 10);
}

function canAcceptSellerRequirementQuotation(item) {
  const ds = String(item.dealStatus || '').toLowerCase();
  if (ds !== 'submitted') return false;
  const rid = item.infrastructureLeasingRequirementId;
  if (rid == null || rid === '' || !Number.isFinite(Number(rid)) || Number(rid) < 1) return false;
  const q = item.sellerRequirementQuotation;
  return Boolean(q && String(q.quotedPrice || '').trim());
}

function dealBadgeClass(dealStatus, naturalLeaseCompleted) {
  if (naturalLeaseCompleted) return 'text-bg-info text-dark';
  const d = String(dealStatus || '').toLowerCase();
  if (d === 'accepted') return 'text-bg-success';
  if (d === 'completed') return 'text-bg-info text-dark';
  if (d === 'rejected') return 'text-bg-danger';
  if (d === 'withdrawn') return 'text-bg-secondary';
  if (d === 'pending_signature') return 'text-bg-warning text-dark';
  return 'text-bg-secondary';
}

function RatingStars({ rating }) {
  const r = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  return (
    <span className="text-warning text-nowrap" role="img" aria-label={`${r} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <FaStar
          key={i}
          className={i <= r ? '' : 'text-secondary opacity-25'}
          style={{ fontSize: '0.8rem' }}
          aria-hidden
        />
      ))}
    </span>
  );
}

/** Visible summary: prefer title, else truncated comment. */
function reviewSummaryLine(sellerReview) {
  if (!sellerReview) return '';
  const t = sellerReview.title != null && String(sellerReview.title).trim();
  if (t) return String(sellerReview.title).trim();
  const c = sellerReview.comment != null && String(sellerReview.comment).trim();
  if (c) return truncateMessage(c, 88);
  return '';
}

/**
 * Compact rating + title in list; expandable full comment (per enquiry id).
 */
function BuyerReviewCollapsible({ enquiryId, sellerReview, expanded, onToggle, compact = false }) {
  const hasReview = Boolean(sellerReview);
  const comment =
    sellerReview?.comment != null && String(sellerReview.comment).trim()
      ? String(sellerReview.comment).trim()
      : '';
  const summary = hasReview ? reviewSummaryLine(sellerReview) : '';
  const canExpand = Boolean(comment);

  if (!hasReview) {
    return null;
  }

  const bodyId = `ile-buyer-review-desc-${enquiryId}`;
  const toggleId = `ile-buyer-review-toggle-${enquiryId}`;

  return (
    <div className={compact ? '' : 'ile-buyer-enquiries-review-compact border rounded px-2 py-2 bg-light'}>
      <div className="d-flex align-items-start gap-2">
        <div className="flex-shrink-0 pt-0">
          <RatingStars rating={sellerReview.rating} />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="fw-semibold text-body small">{summary || 'Your review'}</div>
          {sellerReview.createdAt ? (
            <div className="small text-muted">{formatDate(sellerReview.createdAt)}</div>
          ) : null}
          {canExpand ? (
            <button
              type="button"
              id={toggleId}
              className="btn btn-link btn-sm p-0 ile-buyer-review-toggle mt-1"
              onClick={() => onToggle(enquiryId)}
              aria-expanded={expanded}
              aria-controls={bodyId}
            >
              <span className="small d-inline-flex align-items-center gap-1">
                {expanded ? (
                  <>
                    Hide details
                    <FaChevronUp aria-hidden size={11} />
                  </>
                ) : (
                  <>
                    Show full review
                    <FaChevronDown aria-hidden size={11} />
                  </>
                )}
              </span>
            </button>
          ) : null}
        </div>
      </div>
      {canExpand ? (
        <div
          className={`ile-buyer-review-expandable${expanded ? ' is-open' : ''}`}
          id={bodyId}
          role="region"
          aria-labelledby={toggleId}
        >
          <div className="ile-buyer-review-expandable-inner">
            <p className="small text-secondary mb-0 mt-2 pt-2 border-top ile-buyer-review-full-text">{comment}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function InfrastructureLeasingBuyerEnquiriesPage() {
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(readStoredLayout);
  const [rejectionDetailItem, setRejectionDetailItem] = useState(null);
  const [reviewModalItem, setReviewModalItem] = useState(null);
  const [acceptQuotationItem, setAcceptQuotationItem] = useState(null);
  const [acceptQuotationSubmitting, setAcceptQuotationSubmitting] = useState(false);
  /** Phase 9: disable duplicate lifecycle actions per enquiry row */
  const [withdrawingEnquiryId, setWithdrawingEnquiryId] = useState(null);
  const [unwindingEnquiryId, setUnwindingEnquiryId] = useState(null);
  /** Which enquiry rows have the review description expanded */
  const [expandedBuyerReviews, setExpandedBuyerReviews] = useState(() => new Set());

  const toggleBuyerReviewExpand = useCallback((enquiryId) => {
    const id = Math.trunc(Number(enquiryId));
    if (!Number.isFinite(id)) return;
    setExpandedBuyerReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyInfrastructureEnquiries({ page, pageSize });
      if (res?.success && res.data) {
        const d = res.data;
        setItems(Array.isArray(d.items) ? d.items : []);
        setTotal(Number(d.total) || 0);
        setTotalPages(Number(d.totalPages) || 0);
      } else {
        setItems([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not load your enquiries.');
      setItems([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0 results';
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Showing ${start}–${end} of ${total}`;
  }, [total, page, pageSize]);

  const acceptQuotationModalDates = useMemo(() => {
    if (!acceptQuotationItem) return { start: '', end: '' };
    const start = isoDatePrefixFromEnquiry(acceptQuotationItem.startDate);
    const end = addMonthsToIsoStart(start, acceptQuotationItem.durationMonths);
    return { start, end };
  }, [acceptQuotationItem]);

  const goPage = (next) => {
    if (next < 1 || (totalPages > 0 && next > totalPages)) return;
    setPage(next);
  };

  /**
   * pending_signature → Sign agreement (seller flow / early buyer review).
   * accepted + seller signed, buyer not → Sign agreement (buyer completes signature).
   * Otherwise accepted → View agreement.
   */
  const agreementCta = (item) => {
    const ds = String(item.dealStatus || '').toLowerCase();
    if (ds !== 'pending_signature' && ds !== 'accepted' && ds !== 'completed') return null;
    const to = `/infrastructure-leasing/my-enquiries/${item.infrastructureListingId}/${item.id}/agreement`;
    const sellerSigned = Boolean(item.agreementSellerSignedAt);
    const buyerSigned = Boolean(item.agreementBuyerSignedAt);
    let label = 'View agreement';
    let btnClass = 'btn-outline-dark';
    if (ds === 'pending_signature') {
      label = 'Sign agreement';
      btnClass = 'btn-dark';
    } else if (ds === 'accepted' && sellerSigned && !buyerSigned) {
      label = 'Sign agreement';
      btnClass = 'btn-dark';
    }
    return (
      <Link to={to} className={`btn btn-sm text-center text-decoration-none ${btnClass}`}>
        {label}
      </Link>
    );
  };

  /** After buyer signed agreement: payment → invoice → lease tracking */
  const postSalePipelineBtn = (item) => {
    const ds = String(item.dealStatus || '').toLowerCase();
    if (ds !== 'accepted' && ds !== 'completed') return null;
    if (!item.agreementBuyerSignedAt) return null;
    const stage = String(item.buyerPostSaleStage || '').toLowerCase();
    const base = `/infrastructure-leasing/my-enquiries/${item.infrastructureListingId}/${item.id}`;
    if (stage === 'payment_due') {
      return (
        <Link to={`${base}/payment`} className="btn btn-sm btn-dark text-center text-decoration-none">
          Complete payment
        </Link>
      );
    }
    if (stage === 'invoice_issued') {
      return (
        <Link to={`${base}/invoice`} className="btn btn-sm btn-outline-dark text-center text-decoration-none">
          View invoice
        </Link>
      );
    }
    if (stage === 'lease_active' || stage === 'lease_completed') {
      return (
        <Link to={`${base}/lease-tracking`} className="btn btn-sm btn-outline-secondary text-center text-decoration-none">
          {stage === 'lease_completed' ? 'Lease history' : 'Lease tracking'}
        </Link>
      );
    }
    return null;
  };

  const rejectedDetailsBtn = (item) => {
    const ds = String(item.dealStatus || '').toLowerCase();
    if (ds !== 'rejected') return null;
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-danger text-center"
        onClick={() => setRejectionDetailItem(item)}
      >
        Rejected details
      </button>
    );
  };

  const canSubmitSellerReview = (item) => {
    const ds0 = String(item.dealStatus || '').toLowerCase();
    if (ds0 !== 'accepted' && ds0 !== 'completed') return false;
    if (!item.buyerPaymentCompleted) return false;
    if (item.sellerReview) return false;
    if (REVIEW_REQUIRES_LEASE_ACTIVE) {
      const st = String(item.buyerPostSaleStage || '').toLowerCase();
      return st === 'lease_active' || st === 'lease_completed';
    }
    return true;
  };

  const submitReviewBtn = (item) =>
    canSubmitSellerReview(item) ? (
      <button
        type="button"
        className="btn btn-sm btn-outline-primary text-center"
        onClick={() => setReviewModalItem(item)}
      >
        Submit Review &amp; Rating
      </button>
    ) : null;

  const acceptQuotationCta = (item) =>
    canAcceptSellerRequirementQuotation(item) ? (
      <button
        type="button"
        className="btn btn-sm btn-success text-center"
        onClick={() => setAcceptQuotationItem(item)}
      >
        Accept quotation
      </button>
    ) : null;

  const handleWithdrawSubmittedEnquiry = async (item) => {
    if (
      !window.confirm(
        'Withdraw this enquiry? The seller will no longer see it as an active lead for this listing.'
      )
    ) {
      return;
    }
    setWithdrawingEnquiryId(item.id);
    try {
      const res = await postBuyerWithdrawSubmittedEnquiry(item.infrastructureListingId, item.id);
      if (res?.success) {
        toast.success(res.message || 'Enquiry withdrawn.');
        void load();
      } else {
        toast.error(res?.message || 'Could not withdraw enquiry.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not withdraw enquiry.');
    } finally {
      setWithdrawingEnquiryId(null);
    }
  };

  const handleUnwindPendingSignatureDeal = async (item) => {
    if (
      !window.confirm(
        'Cancel this pending deal? Reserved capacity (if any) will be released and the agreement will be cancelled.'
      )
    ) {
      return;
    }
    setUnwindingEnquiryId(item.id);
    try {
      const res = await postBuyerUnwindPendingSignatureDeal(item.infrastructureListingId, item.id, {});
      if (res?.success) {
        toast.success(res.message || 'Pending deal cancelled.');
        void load();
      } else {
        toast.error(res?.message || 'Could not cancel pending deal.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not cancel pending deal.');
    } finally {
      setUnwindingEnquiryId(null);
    }
  };

  const withdrawSubmittedBtn = (item) => {
    const ds = String(item.dealStatus || '').toLowerCase();
    if (ds !== 'submitted') return null;
    const busy = withdrawingEnquiryId === item.id;
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary text-center"
        disabled={busy}
        onClick={() => void handleWithdrawSubmittedEnquiry(item)}
      >
        {busy ? 'Withdrawing…' : 'Withdraw enquiry'}
      </button>
    );
  };

  const unwindPendingSignatureBtn = (item) => {
    const ds = String(item.dealStatus || '').toLowerCase();
    if (ds !== 'pending_signature') return null;
    const busy = unwindingEnquiryId === item.id;
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-danger text-center"
        disabled={busy}
        onClick={() => void handleUnwindPendingSignatureDeal(item)}
      >
        {busy ? 'Cancelling…' : 'Cancel pending deal'}
      </button>
    );
  };

  const confirmAcceptRequirementQuotation = async ({ agreementStartDate, agreementEndDate, additionalTerms }) => {
    if (!acceptQuotationItem) return;
    setAcceptQuotationSubmitting(true);
    try {
      const body = {
        agreementStartDate,
        agreementEndDate,
        ...(additionalTerms ? { additionalTerms } : {}),
      };
      const res = await postBuyerAcceptRequirementQuotation(
        acceptQuotationItem.infrastructureListingId,
        acceptQuotationItem.id,
        body
      );
      if (res?.success) {
        toast.success(res.message || 'Quotation accepted.');
        setAcceptQuotationItem(null);
        void load();
      } else {
        toast.error(res?.message || 'Could not accept quotation.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not accept quotation.');
    } finally {
      setAcceptQuotationSubmitting(false);
    }
  };

  return (
    <div className="container-fluid ile-buyer-enquiries py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/buyer">Buyer dashboard</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Infrastructure leasing — My enquiries
          </li>
        </ol>
      </nav>

      <div className="mb-3">
        <h1 className="h3 mb-1">My enquiry list</h1>
        <p className="text-muted mb-0 small">
          Enquiries you submitted on infrastructure &amp; asset listings ({rangeLabel}).
        </p>
      </div>

      {loading ? (
        <div className="text-muted py-5">Loading enquiries…</div>
      ) : items.length === 0 ? (
        <div className="border rounded p-5 text-center bg-white ile-buyer-enquiries-empty">
          <p className="mb-3 text-muted">You haven’t submitted any enquiries yet.</p>
          <Link to="/infrastructure-leasing/listings" className="btn btn-primary">
            Browse listings
          </Link>
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ile-buyer-enquiries-toolbar">
            <span className="small text-muted">{rangeLabel}</span>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="small text-muted d-none d-sm-inline">Layout</span>
              <div className="ile-search-view-toggles" role="group" aria-label="Choose layout">
                <button
                  type="button"
                  className={viewMode === 'cards' ? 'active' : ''}
                  onClick={() => setViewMode('cards')}
                  title="Cards"
                  aria-pressed={viewMode === 'cards'}
                  aria-label="Card layout"
                >
                  <FaTh aria-hidden />
                </button>
                <button
                  type="button"
                  className={viewMode === 'table' ? 'active' : ''}
                  onClick={() => setViewMode('table')}
                  title="Table"
                  aria-pressed={viewMode === 'table'}
                  aria-label="Table layout"
                >
                  <FaTable aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'cards' ? (
            <div className="row g-3">
              {items.map((item) => (
                <div key={item.id} className="col-12 col-md-6 col-xl-4 d-flex">
                  <div className="card ile-buyer-enquiries-card h-100 w-100 border shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <span className={`badge ${dealBadgeClass(item.dealStatus, item.naturalLeaseCompleted)}`}>
                          {item.displayStatusLabel ?? item.statusLabel}
                        </span>
                        <span className="badge bg-light text-dark border">{urgencyLabel(item.urgency)}</span>
                      </div>
                      <h2 className="h6 card-title">{item.displayName}</h2>
                      <p className="small text-muted mb-1">Listing #{item.infrastructureListingId}</p>
                      {item.assetLocation && (
                        <p className="small text-muted mb-2 d-flex gap-1">
                          <FaMapMarkerAlt className="flex-shrink-0 mt-1" aria-hidden />
                          <span>{item.assetLocation}</span>
                        </p>
                      )}
                      <p className="small mb-2">
                        <strong>Start:</strong> {item.startDate || '—'}{' '}
                        <span className="text-muted">·</span>{' '}
                        <strong>Duration:</strong>{' '}
                        {item.durationMonths != null && item.durationMonths > 0
                          ? `${item.durationMonths} mo`
                          : '—'}
                      </p>
                      {item.budget ? (
                        <p className="small mb-2">
                          <strong>Budget:</strong> {item.budget}
                        </p>
                      ) : null}
                      <div className="small mb-3 flex-grow-1">
                        <strong className="d-block mb-1">Message</strong>
                        {item.message && String(item.message).trim() ? (
                          <HomeClampedText text={String(item.message)} className="mb-0 text-secondary" />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                      <div className="small text-muted mb-2">Submitted {formatDate(item.createdAt)}</div>
                      {item.sellerReview ? (
                        <div className="mb-3">
                          <BuyerReviewCollapsible
                            enquiryId={item.id}
                            sellerReview={item.sellerReview}
                            expanded={expandedBuyerReviews.has(item.id)}
                            onToggle={toggleBuyerReviewExpand}
                          />
                        </div>
                      ) : null}
                      <div className="mt-auto d-flex flex-column gap-2 align-self-stretch">
                        {rejectedDetailsBtn(item)}
                        {withdrawSubmittedBtn(item)}
                        {acceptQuotationCta(item)}
                        {postSalePipelineBtn(item)}
                        {submitReviewBtn(item)}
                        {unwindPendingSignatureBtn(item)}
                        {agreementCta(item)}
                        <Link
                          to={`/infrastructure-leasing/listing/${item.infrastructureListingId}?tab=${listingTabForEnquiry(item)}`}
                          className="btn btn-dark btn-sm text-center text-decoration-none"
                        >
                          View listing <FaExternalLinkAlt className="ms-1" size={12} aria-hidden />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-responsive border rounded bg-white ile-buyer-enquiries-table-wrap">
              <table className="table table-hover align-middle mb-0 ile-buyer-enquiries-table">
                <thead className="table-light">
                  <tr>
                    <th className="text-muted small text-uppercase">Asset / property</th>
                    <th className="text-muted small text-uppercase">Listing</th>
                    <th className="text-muted small text-uppercase">Location</th>
                    <th className="text-muted small text-uppercase">Enquiry date</th>
                    <th className="text-muted small text-uppercase">Status</th>
                    <th className="text-muted small text-uppercase">Priority</th>
                    <th className="text-muted small text-uppercase">Message</th>
                    <th className="text-muted small text-uppercase">
                      <span className="visually-hidden">Review</span>
                    </th>
                    <th className="text-end text-muted small text-uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-medium">{item.displayName}</td>
                      <td className="small text-muted">#{item.infrastructureListingId}</td>
                      <td className="small">{item.assetLocation || '—'}</td>
                      <td className="small text-nowrap">{formatDate(item.createdAt)}</td>
                      <td>
                        <span className={`badge ${dealBadgeClass(item.dealStatus, item.naturalLeaseCompleted)}`}>
                          {item.displayStatusLabel ?? item.statusLabel}
                        </span>
                      </td>
                      <td className="small">{urgencyLabel(item.urgency)}</td>
                      <td className="small" style={{ maxWidth: 260 }}>
                        <span title={item.message}>{truncateMessage(item.message, 100)}</span>
                      </td>
                      <td className="small ile-buyer-enquiries-table-review-cell" style={{ minWidth: 200, maxWidth: 280 }}>
                        {item.sellerReview ? (
                          <BuyerReviewCollapsible
                            enquiryId={item.id}
                            sellerReview={item.sellerReview}
                            expanded={expandedBuyerReviews.has(item.id)}
                            onToggle={toggleBuyerReviewExpand}
                            compact
                          />
                        ) : null}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex flex-column gap-1 align-items-end">
                          {rejectedDetailsBtn(item)}
                          {withdrawSubmittedBtn(item)}
                          {acceptQuotationCta(item)}
                          {postSalePipelineBtn(item)}
                          {submitReviewBtn(item)}
                          {unwindPendingSignatureBtn(item)}
                          {agreementCta(item)}
                          <Link
                            to={`/infrastructure-leasing/listing/${item.infrastructureListingId}?tab=${listingTabForEnquiry(item)}`}
                            className="btn btn-sm btn-outline-dark"
                          >
                            View listing <FaExternalLinkAlt className="ms-1" size={12} aria-hidden />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <nav
              className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4"
              aria-label="Enquiry pages"
            >
              <span className="small text-muted">{rangeLabel}</span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => goPage(page - 1)}
                  aria-label="Previous page"
                >
                  <FaChevronLeft aria-hidden />
                </button>
                <span className="btn btn-sm btn-outline-secondary disabled text-dark">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => goPage(page + 1)}
                  aria-label="Next page"
                >
                  <FaChevronRight aria-hidden />
                </button>
              </div>
            </nav>
          )}
        </>
      )}

      <InfrastructureLeasingAcceptRequirementQuotationModal
        open={Boolean(acceptQuotationItem)}
        onClose={() => {
          if (!acceptQuotationSubmitting) setAcceptQuotationItem(null);
        }}
        listingLabel={
          acceptQuotationItem
            ? acceptQuotationItem.displayName || `Listing #${acceptQuotationItem.infrastructureListingId}`
            : ''
        }
        quotedPrice={acceptQuotationItem?.sellerRequirementQuotation?.quotedPrice || ''}
        initialAgreementStartDate={acceptQuotationModalDates.start}
        initialAgreementEndDate={acceptQuotationModalDates.end}
        submitting={acceptQuotationSubmitting}
        onConfirm={confirmAcceptRequirementQuotation}
      />

      <InfrastructureLeasingRejectionDetailsModal
        open={Boolean(rejectionDetailItem)}
        onClose={() => setRejectionDetailItem(null)}
        listingLabel={
          rejectionDetailItem
            ? rejectionDetailItem.displayName || `Listing #${rejectionDetailItem.infrastructureListingId}`
            : ''
        }
        rejectionReason={rejectionDetailItem?.rejectionReason}
        rejectionComments={rejectionDetailItem?.rejectionComments}
        rejectedAt={rejectionDetailItem?.rejectedAt}
      />

      <InfrastructureLeasingSellerReviewModal
        open={Boolean(reviewModalItem)}
        onClose={() => setReviewModalItem(null)}
        listingId={reviewModalItem?.infrastructureListingId}
        enquiryId={reviewModalItem?.id}
        listingTitle={reviewModalItem?.listingTitle || reviewModalItem?.displayName}
        assetLabel={reviewModalItem?.displayName}
        onSuccess={() => {
          void load();
        }}
      />
    </div>
  );
}
